import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  ActivityIndicator,
  Alert,
  Image,
  Linking,
  Modal,
  Pressable,
  RefreshControl,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View,
} from 'react-native';
import Icon from 'react-native-vector-icons/Feather';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { useFocusEffect, useNavigation, useRoute, type RouteProp } from '@react-navigation/native';
import type { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { doc, getDoc, onSnapshot } from 'firebase/firestore';
import TonyteBackground from '../components/TonyteBackground';
import PickerField from '../components/PickerField';
import { auth, firestore } from '../firebase';
import { normalizeActivity } from '../hooks/useFeed';
import { Typography } from '../theme';
import { useTheme } from '../theme/useTheme';
import type { TonyteTheme } from '../theme/themes';
import { useI18n } from '../i18n';
import type { RootStackParamList, TonyteActivity } from '../types';
import {
  CALENDAR_STATUSES,
  berlinTodayKey,
  calendarStatus,
  dayLabel,
  eventSource,
  expandRecurrence,
  formatBerlinShort,
  groupByDay,
  isLegacyRecurring,
  missingDetails,
  monthGrid,
  scheduledPublishMillis,
  type CalendarStatus,
} from '../utils/partnerCalendar';
import { berlinDateTimeToMillis } from '../utils/promotionAdmin';
import {
  cancelEvent,
  cancelSeriesFromDate,
  deleteEvent,
  detachFromLuma,
  duplicateEvent,
  getSeries,
  listPartnerEvents,
  publishEvent,
  publishSeriesFromDate,
  restoreEvent,
  schedulePublish,
  unpublishEvent,
} from '../services/partnerCalendar';

type Raw = TonyteActivity & Record<string, any>;
type Entry = { id: string; raw: Raw; status: CalendarStatus; source: ReturnType<typeof eventSource>; missing: string[] };
type Connection = { provider?: string; paused?: boolean; ambiguousMatches?: Array<{ title: string }> } | null;

const PORTAL_URL = 'https://tonyte.app/partners/dashboard/?section=calendar';
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];
const WEEKDAYS = ['M', 'T', 'W', 'T', 'F', 'S', 'S'];

export const STATUS_COLORS: Record<CalendarStatus, string> = {
  draft: '#F59E0B',
  scheduled: '#38BDF8',
  published: '#22C55E',
  cancelled: '#F87171',
  past: '#94A3B8',
};

// The partner's working calendar in the app — same events, states and
// actions as the portal's Calendar (Tonyte_website partnerCalendarPanel.js).
const PartnerCalendarScreen: React.FC = () => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createThemedStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const navigation = useNavigation<NativeStackNavigationProp<RootStackParamList>>();
  const route = useRoute<RouteProp<RootStackParamList, 'PartnerCalendar'>>();
  const ownerUid = route.params?.ownerUid || auth.currentUser?.uid || '';
  const today = berlinTodayKey();
  const [ym, setYm] = useState(() => ({ year: Number(today.slice(0, 4)), month: Number(today.slice(5, 7)) }));
  const [view, setView] = useState<'month' | 'list'>('month');
  const [filter, setFilter] = useState<'all' | CalendarStatus>('all');
  const [needsDetails, setNeedsDetails] = useState(false);
  const [listScope, setListScope] = useState<'upcoming' | 'past'>('upcoming');
  const [selectedDay, setSelectedDay] = useState(today);
  const [events, setEvents] = useState<Raw[]>([]);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [connection, setConnection] = useState<Connection>(null);
  const [openId, setOpenId] = useState<string | null>(null);

  const load = useCallback(async () => {
    if (!ownerUid) return;
    try {
      setError(null);
      setEvents(await listPartnerEvents(ownerUid));
    } catch (e) {
      setError(e instanceof Error ? e.message : String(e));
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [ownerUid]);

  useFocusEffect(useCallback(() => { void load(); }, [load]));
  useEffect(() => {
    if (!ownerUid) return undefined;
    return onSnapshot(doc(firestore, 'partnerCalendarIntegrations', ownerUid), snap => setConnection(snap.exists() ? (snap.data() as Connection) : null), () => setConnection(null));
  }, [ownerUid]);

  const entries = useMemo<Entry[]>(() => {
    const now = Date.now();
    return events.map(raw => {
      const status = calendarStatus(raw, now);
      return { id: raw.id, raw, status, source: eventSource(raw), missing: status === 'past' || status === 'cancelled' ? [] : missingDetails(raw).required };
    });
  }, [events]);
  const counts = useMemo(() => {
    const c: Record<string, number> = { all: entries.length, needs: entries.filter(e => e.missing.length).length };
    CALENDAR_STATUSES.forEach(s => { c[s] = entries.filter(e => e.status === s).length; });
    return c;
  }, [entries]);
  const visible = entries.filter(e => (filter === 'all' || e.status === filter) && (!needsDetails || e.missing.length > 0));
  const byDay = useMemo(() => groupByDay(visible), [visible]);
  const open = entries.find(e => e.id === openId) || null;

  const createOn = (dateKey: string) => navigation.navigate('UploadEvent', { initialDate: dateKey });

  const renderRow = (e: Entry) => (
    <TouchableOpacity key={`${e.id}`} style={[styles.row, themed.surface]} onPress={() => setOpenId(e.id)} accessibilityRole="button">
      <View style={[styles.rowBar, { backgroundColor: STATUS_COLORS[e.status] }]} />
      <View style={styles.rowTime}>
        <Text style={[styles.rowTimeText, themed.textPrimary]}>{e.raw.startTime || '—'}</Text>
        {e.raw.endTime && e.raw.endTime !== e.raw.startTime ? <Text style={[styles.rowTimeEnd, themed.textMuted]}>{e.raw.endTime}</Text> : null}
      </View>
      <View style={styles.rowMain}>
        <Text style={[styles.rowTitle, themed.textPrimary, e.status === 'cancelled' && styles.strike]} numberOfLines={1}>{e.raw.title || t('partnerCalendar.untitled')}</Text>
        <Text style={[styles.rowMeta, themed.textSecondary]} numberOfLines={1}>{e.raw.locationName || e.raw.venueName || e.raw.location || t('partnerCalendar.noLocation')}</Text>
        <View style={styles.badges}>
          <StatusPill status={e.status} label={t(`partnerCalendar.status.${e.status}`)} />
          {e.source !== 'tonyte' ? <Badge label={t(`partnerCalendar.source.${e.source}`)} tone="luma" themed={themed} /> : null}
          {e.raw.seriesId ? <Badge label={`↻ ${t('partnerCalendar.repeats')}`} themed={themed} /> : null}
          {e.missing.length ? <Badge label={t('partnerCalendar.missingCount').replace('{count}', String(e.missing.length))} tone="warn" themed={themed} /> : null}
        </View>
      </View>
      <Icon name="chevron-right" size={16} color={theme.colors.iconMuted} />
    </TouchableOpacity>
  );

  const renderDays = (keys: string[]) => keys.map(key => (
    <View key={key} style={styles.day}>
      <Text style={[styles.dayHeading, themed.textMuted]}>{dayLabel(key)}{key === today ? ` · ${t('partnerCalendar.today')}` : ''}</Text>
      {(byDay.get(key) || []).map(renderRow)}
    </View>
  ));

  const grid = monthGrid(ym.year, ym.month);
  const monthPrefix = `${ym.year}-${String(ym.month).padStart(2, '0')}`;
  const listKeys = [...byDay.keys()].filter(k => (listScope === 'upcoming' ? k >= today : k < today)).sort();
  if (listScope === 'past') listKeys.reverse();
  const ambiguous = connection?.ambiguousMatches || [];

  return (
    <TonyteBackground appearanceAware style={[styles.container, themed.container]}>
      <View style={[styles.header, { paddingTop: insets.top + 10 }]}>
        <TouchableOpacity style={[styles.iconButton, themed.surface]} onPress={() => navigation.goBack()} accessibilityRole="button" accessibilityLabel={t('common.back')}>
          <Icon name="arrow-left" size={20} color={theme.colors.icon} />
        </TouchableOpacity>
        <View style={styles.headerCopy}>
          <Text style={[styles.kicker, themed.textMuted]}>{route.params?.partnerName || t('partnerCalendar.kicker')}</Text>
          <Text style={[styles.title, themed.textPrimary]}>{t('partnerCalendar.title')}</Text>
        </View>
        <TouchableOpacity style={styles.newButton} onPress={() => createOn(selectedDay >= today ? selectedDay : today)} accessibilityRole="button">
          <Icon name="plus" size={16} color="#fff" />
          <Text style={styles.newButtonText}>{t('partnerCalendar.new')}</Text>
        </TouchableOpacity>
      </View>

      <ScrollView
        contentContainerStyle={[styles.content, { paddingBottom: insets.bottom + 40 }]}
        refreshControl={<RefreshControl refreshing={refreshing} onRefresh={() => { setRefreshing(true); void load(); }} />}
      >
        <Text style={[styles.intro, themed.textSecondary]}>{t('partnerCalendar.intro')}</Text>
        {connection?.provider === 'luma' ? (
          <View style={[styles.lumaBanner, themed.lumaBanner]}>
            <Text style={[styles.lumaText, themed.textPrimary]}>
              {t('partnerCalendar.lumaConnected')}{connection.paused ? ` (${t('partnerCalendar.paused')})` : ''}
            </Text>
            {ambiguous.length ? (
              <Text style={[styles.lumaText, themed.textSecondary]}>
                {t('partnerCalendar.lumaSkipped').replace('{count}', String(ambiguous.length))}
              </Text>
            ) : null}
          </View>
        ) : null}

        <View style={styles.toolbar}>
          <View style={[styles.segment, themed.surface]}>
            {(['month', 'list'] as const).map(v => (
              <TouchableOpacity key={v} style={[styles.segmentItem, view === v && themed.segmentActive]} onPress={() => setView(v)} accessibilityRole="tab" accessibilityState={{ selected: view === v }}>
                <Text style={[styles.segmentText, view === v ? themed.textPrimary : themed.textMuted]}>{t(`partnerCalendar.view.${v}`)}</Text>
              </TouchableOpacity>
            ))}
          </View>
        </View>
        <ScrollView horizontal showsHorizontalScrollIndicator={false} contentContainerStyle={styles.filters}>
          <Chip label={`${t('partnerCalendar.all')} ${counts.all}`} active={filter === 'all'} onPress={() => setFilter('all')} themed={themed} />
          {CALENDAR_STATUSES.map(s => (
            <Chip key={s} label={`${t(`partnerCalendar.status.${s}`)} ${counts[s]}`} active={filter === s} dot={STATUS_COLORS[s]} onPress={() => setFilter(s)} themed={themed} />
          ))}
          <Chip label={`${t('partnerCalendar.needsDetails')} ${counts.needs}`} active={needsDetails} dot="#FBBF24" onPress={() => setNeedsDetails(v => !v)} themed={themed} />
        </ScrollView>

        {loading ? <ActivityIndicator style={styles.loading} color={theme.colors.accentSecondary} /> : null}
        {error ? <Text style={styles.error}>{error}</Text> : null}

        {!loading && view === 'month' ? (
          <>
            <View style={styles.monthNav}>
              <TouchableOpacity onPress={() => setYm(c => (c.month === 1 ? { year: c.year - 1, month: 12 } : { ...c, month: c.month - 1 }))} accessibilityLabel={t('partnerCalendar.prevMonth')} hitSlop={10}>
                <Icon name="chevron-left" size={22} color={theme.colors.icon} />
              </TouchableOpacity>
              <Text style={[styles.monthTitle, themed.textPrimary]}>{MONTHS[ym.month - 1]} {ym.year}</Text>
              <TouchableOpacity onPress={() => setYm(c => (c.month === 12 ? { year: c.year + 1, month: 1 } : { ...c, month: c.month + 1 }))} accessibilityLabel={t('partnerCalendar.nextMonth')} hitSlop={10}>
                <Icon name="chevron-right" size={22} color={theme.colors.icon} />
              </TouchableOpacity>
            </View>
            <View style={[styles.grid, themed.surface]}>
              {WEEKDAYS.map((d, i) => <Text key={`wd-${i}`} style={[styles.weekday, themed.textMuted]}>{d}</Text>)}
              {grid.flat().map(key => {
                const dayEvents = byDay.get(key) || [];
                const selected = key === selectedDay;
                return (
                  <Pressable
                    key={key}
                    style={[styles.cell, selected && themed.cellSelected]}
                    onPress={() => setSelectedDay(key)}
                    accessibilityRole="button"
                    accessibilityLabel={`${dayLabel(key)}, ${dayEvents.length} ${t('partnerCalendar.events')}`}
                  >
                    <Text style={[
                      styles.cellNum,
                      themed.textPrimary,
                      !key.startsWith(monthPrefix) && styles.cellOutside,
                      key === today && styles.cellToday,
                    ]}>{Number(key.slice(8))}</Text>
                    <View style={styles.dots}>
                      {dayEvents.slice(0, 3).map(e => <View key={`${key}-${e.id}`} style={[styles.dot, { backgroundColor: STATUS_COLORS[e.status] }]} />)}
                    </View>
                  </Pressable>
                );
              })}
            </View>
            <View style={styles.dayPanelHead}>
              <Text style={[styles.dayHeading, themed.textPrimary]}>{dayLabel(selectedDay)}</Text>
              {selectedDay >= today ? (
                <TouchableOpacity style={[styles.addDay, themed.surface]} onPress={() => createOn(selectedDay)} accessibilityRole="button">
                  <Icon name="plus" size={14} color={theme.colors.accentSecondary} />
                  <Text style={[styles.addDayText, themed.accentText]}>{t('partnerCalendar.addOnDay')}</Text>
                </TouchableOpacity>
              ) : null}
            </View>
            {(byDay.get(selectedDay) || []).map(renderRow)}
            {!(byDay.get(selectedDay) || []).length ? <Text style={[styles.empty, themed.textMuted]}>{t('partnerCalendar.nothingThisDay')}</Text> : null}
          </>
        ) : null}

        {!loading && view === 'list' ? (
          <>
            <View style={styles.filters}>
              <Chip label={t('partnerCalendar.upcoming')} active={listScope === 'upcoming'} onPress={() => setListScope('upcoming')} themed={themed} />
              <Chip label={t('partnerCalendar.past')} active={listScope === 'past'} onPress={() => setListScope('past')} themed={themed} />
            </View>
            {listKeys.length ? renderDays(listKeys) : <Text style={[styles.empty, themed.textMuted]}>{t('partnerCalendar.noEvents')}</Text>}
          </>
        ) : null}

        <TouchableOpacity style={styles.portalLink} onPress={() => void Linking.openURL(PORTAL_URL)}>
          <Text style={[styles.portalText, themed.textMuted]}>{t('partnerCalendar.portalHint')}</Text>
        </TouchableOpacity>
      </ScrollView>

      <EventSheet
        entry={open}
        onClose={() => setOpenId(null)}
        onChanged={async () => { await load(); }}
        onEdit={(raw, scope) => { setOpenId(null); navigation.navigate('UploadEvent', { event: raw, mode: 'edit', seriesScope: scope }); }}
        onEditNew={async id => {
          const snap = await getDoc(doc(firestore, 'activities', id));
          if (snap.exists()) navigation.navigate('UploadEvent', { event: { ...normalizeActivity(id, snap.data(), 'activities'), ...snap.data(), id } as any, mode: 'edit' });
        }}
      />
    </TonyteBackground>
  );
};

const EventSheet: React.FC<{
  entry: Entry | null;
  onClose: () => void;
  onChanged: () => Promise<void>;
  onEdit: (raw: Raw, scope: 'one' | 'future') => void;
  onEditNew: (id: string) => Promise<void>;
}> = ({ entry, onClose, onChanged, onEdit, onEditNew }) => {
  const { t } = useI18n();
  const { theme } = useTheme();
  const themed = useMemo(() => createThemedStyles(theme), [theme]);
  const insets = useSafeAreaInsets();
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<{ ok: boolean; text: string } | null>(null);
  const [scope, setScope] = useState<'one' | 'future'>('one');
  const [scheduling, setScheduling] = useState(false);
  const [schedDate, setSchedDate] = useState('');
  const [schedTime, setSchedTime] = useState('');
  const [seriesInfo, setSeriesInfo] = useState<string | null>(null);

  useEffect(() => {
    setMessage(null);
    setScope('one');
    setScheduling(false);
    setSeriesInfo(null);
    if (entry?.raw.seriesId) {
      getSeries(entry.raw.seriesId).then(series => {
        if (!series) return;
        const dates = expandRecurrence(series.rule, series.startDate);
        setSeriesInfo(t('partnerCalendar.seriesInfo').replace('{count}', String(dates.length)).replace('{until}', dates[dates.length - 1] || ''));
      }).catch(() => {});
    }
  }, [entry?.id, entry?.raw.seriesId, t]);

  if (!entry) return null;
  const { raw, status, source } = entry;
  const missing = missingDetails(raw);
  const synced = source === 'luma';
  const isSeries = Boolean(raw.seriesId);
  const scheduledAt = scheduledPublishMillis(raw);

  const run = async (task: () => Promise<unknown>, ok: string | ((r: any) => string)) => {
    setBusy(true);
    try {
      const result = await task();
      await onChanged();
      setMessage({ ok: true, text: typeof ok === 'function' ? ok(result) : ok });
    } catch (e) {
      setMessage({ ok: false, text: e instanceof Error ? e.message : String(e) });
    } finally {
      setBusy(false);
    }
  };
  const confirm = (title: string, body: string, action: string, destructive = false) => new Promise<boolean>(resolve => {
    Alert.alert(title, body, [
      { text: t('common.cancel'), style: 'cancel', onPress: () => resolve(false) },
      { text: action, style: destructive ? 'destructive' : 'default', onPress: () => resolve(true) },
    ]);
  });

  const actions: Array<{ key: string; label: string; primary?: boolean; danger?: boolean; onPress: () => void; disabled?: boolean }> = [];
  if (status !== 'past') actions.push({ key: 'edit', label: t('partnerCalendar.action.edit'), onPress: () => onEdit(raw, scope) });
  actions.push({ key: 'dup', label: t('partnerCalendar.action.duplicate'), onPress: () => run(async () => { const id = await duplicateEvent(raw.id); onClose(); await onEditNew(id); }, t('partnerCalendar.done.duplicated')) });
  if (status === 'draft' || status === 'scheduled') {
    actions.push({
      key: 'publish', label: t('partnerCalendar.action.publish'), primary: true, disabled: missing.required.length > 0,
      onPress: async () => {
        if (!(await confirm(t('partnerCalendar.action.publish'), scope === 'future' ? t('partnerCalendar.confirm.publishFuture') : t('partnerCalendar.confirm.publish'), t('partnerCalendar.action.publish')))) return;
        void run(() => (scope === 'future' ? publishSeriesFromDate(raw.id) : publishEvent(raw.id)), n => (typeof n === 'number' && n > 1 ? t('partnerCalendar.done.publishedMany').replace('{count}', String(n)) : t('partnerCalendar.done.published')));
      },
    });
    if (!isSeries) actions.push({ key: 'schedule', label: t('partnerCalendar.action.schedule'), onPress: () => setScheduling(v => !v) });
  }
  if (status === 'published') {
    actions.push({ key: 'unpublish', label: t('partnerCalendar.action.unpublish'), onPress: async () => {
      if (!(await confirm(t('partnerCalendar.action.unpublish'), t('partnerCalendar.confirm.unpublish'), t('partnerCalendar.action.unpublish')))) return;
      void run(() => unpublishEvent(raw.id), t('partnerCalendar.done.unpublished'));
    } });
  }
  if (status === 'draft' || status === 'scheduled' || status === 'published') {
    actions.push({ key: 'cancel', label: t('partnerCalendar.action.cancel'), onPress: async () => {
      const note = synced ? `\n\n${t('partnerCalendar.luma.cancel')}` : '';
      if (!(await confirm(t('partnerCalendar.action.cancel'), `${scope === 'future' ? t('partnerCalendar.confirm.cancelFuture') : t('partnerCalendar.confirm.cancel')}${note}`, t('partnerCalendar.action.cancel'), true))) return;
      void run(() => (scope === 'future' ? cancelSeriesFromDate(raw.id) : cancelEvent(raw.id)), n => (typeof n === 'number' && n > 1 ? t('partnerCalendar.done.cancelledMany').replace('{count}', String(n)) : t('partnerCalendar.done.cancelled')));
    } });
  }
  if (status === 'cancelled') actions.push({ key: 'restore', label: t('partnerCalendar.action.restore'), onPress: () => void run(() => restoreEvent(raw.id), t('partnerCalendar.done.restored')) });
  if (synced) {
    actions.push({ key: 'detach', label: t('partnerCalendar.action.detach'), onPress: async () => {
      if (!(await confirm(t('partnerCalendar.action.detach'), t('partnerCalendar.confirm.detach'), t('partnerCalendar.action.detach')))) return;
      void run(() => detachFromLuma(raw.id), t('partnerCalendar.done.detached'));
    } });
  }
  actions.push({ key: 'delete', label: t('partnerCalendar.action.delete'), danger: true, onPress: async () => {
    const extra = isSeries ? `\n\n${t('partnerCalendar.confirm.deleteSeriesDate')}` : synced ? `\n\n${t('partnerCalendar.luma.delete')}` : '';
    if (!(await confirm(t('partnerCalendar.action.delete'), `${t('partnerCalendar.confirm.delete').replace('{title}', raw.title || '')}${extra}`, t('partnerCalendar.action.delete'), true))) return;
    void run(async () => { await deleteEvent(raw.id); onClose(); }, t('partnerCalendar.done.deleted'));
  } });

  return (
    <Modal visible transparent animationType="slide" onRequestClose={onClose}>
      <View style={styles.sheetRoot}>
      <Pressable style={[StyleSheet.absoluteFill, styles.sheetBackdrop]} onPress={onClose} accessibilityLabel={t('common.done')} />
      <View style={[styles.sheet, themed.sheet, { paddingBottom: insets.bottom + 16 }]}>
        <View style={[styles.grabber, { backgroundColor: theme.colors.border }]} />
        <ScrollView contentContainerStyle={styles.sheetContent}>
          <View style={styles.badges}>
            <StatusPill status={status} label={t(`partnerCalendar.status.${status}`)} />
            <Badge label={t(`partnerCalendar.source.${source}`)} tone={source.startsWith('luma') ? 'luma' : undefined} themed={themed} />
            {isSeries ? <Badge label={`↻ ${t('partnerCalendar.repeats')}`} themed={themed} /> : null}
          </View>
          {raw.coverPhoto || raw.coverImageUrl ? <Image source={{ uri: raw.coverPhoto || raw.coverImageUrl }} style={styles.cover} resizeMode="cover" /> : null}
          <Text style={[styles.sheetTitle, themed.textPrimary]}>{raw.title || t('partnerCalendar.untitled')}</Text>
          <Fact label={t('partnerCalendar.when')} value={`${dayLabel(raw.startDate)}${raw.endDate && raw.endDate !== raw.startDate ? ` → ${dayLabel(raw.endDate)}` : ''} · ${[raw.startTime, raw.endTime].filter(Boolean).join('–') || '—'} (Berlin)`} themed={themed} />
          <Fact label={t('partnerCalendar.where')} value={[raw.locationName || raw.venueName, raw.address].filter(Boolean).join(' · ') || t('partnerCalendar.noLocation')} themed={themed} />
          <Fact label={t('partnerCalendar.entry')} value={raw.isFree === true ? t('partnerCalendar.free') : raw.requiresTicket ? `${t('partnerCalendar.paid')}${raw.ticketPrice ? ` · ${raw.ticketPrice}` : ''}` : t('partnerCalendar.notConfirmed')} themed={themed} />
          {seriesInfo ? <Fact label={t('partnerCalendar.repeats')} value={`${seriesInfo}${raw.seriesException ? ` · ${t('partnerCalendar.editedSeparately')}` : ''}`} themed={themed} /> : null}
          {status === 'scheduled' ? <Fact label={t('partnerCalendar.goesLive')} value={`${formatBerlinShort(scheduledAt)} (Berlin)`} themed={themed} /> : null}
          {raw.scheduleError ? <Text style={styles.errorBox}>{t('partnerCalendar.scheduleFailed')}: {raw.scheduleError}</Text> : null}
          {isLegacyRecurring(raw) ? <Text style={styles.warnBox}>{t('partnerCalendar.legacyRecurring')}</Text> : null}
          {missing.required.length && status !== 'past' && status !== 'cancelled' ? (
            <Text style={styles.warnBox}>{t('partnerCalendar.beforePublishing')}: {missing.required.join(', ')}</Text>
          ) : null}
          {source === 'luma' || source === 'luma-detached' ? (
            <View style={[styles.lumaBanner, themed.lumaBanner]}>
              <Text style={[styles.lumaText, themed.textPrimary]}>{synced ? t('partnerCalendar.luma.synced') : t('partnerCalendar.luma.detached')}</Text>
              {synced ? <Text style={[styles.lumaText, themed.textSecondary]}>• {t('partnerCalendar.luma.cancel')}{'\n'}• {t('partnerCalendar.luma.delete')}{'\n'}• {t('partnerCalendar.luma.detach')}</Text> : null}
            </View>
          ) : null}
          {isSeries ? (
            <View style={[styles.scopeBox, themed.surface]}>
              <Text style={[styles.scopeLegend, themed.textMuted]}>{t('partnerCalendar.scopeLegend')}</Text>
              {(['one', 'future'] as const).map(s => (
                <TouchableOpacity key={s} style={styles.scopeRow} onPress={() => setScope(s)} accessibilityRole="radio" accessibilityState={{ checked: scope === s }}>
                  <Icon name={scope === s ? 'check-circle' : 'circle'} size={16} color={scope === s ? theme.colors.accentSecondary : theme.colors.iconMuted} />
                  <Text style={[styles.scopeText, themed.textPrimary]}>{t(`partnerCalendar.scope.${s}`)}</Text>
                </TouchableOpacity>
              ))}
            </View>
          ) : null}
          {message ? <Text style={[styles.message, message.ok ? styles.messageOk : styles.messageError]} accessibilityLiveRegion="polite">{message.text}</Text> : null}
          <View style={styles.actions}>
            {actions.map(a => (
              <TouchableOpacity
                key={a.key}
                disabled={busy || a.disabled}
                onPress={a.onPress}
                style={[styles.action, a.primary ? styles.actionPrimary : themed.surface, (busy || a.disabled) && styles.disabled]}
                accessibilityRole="button"
              >
                <Text style={[styles.actionText, a.primary ? styles.actionPrimaryText : a.danger ? styles.dangerText : themed.textPrimary]}>{a.label}</Text>
              </TouchableOpacity>
            ))}
          </View>
          {scheduling ? (
            <View style={[styles.scopeBox, themed.surface]}>
              <Text style={[styles.scopeLegend, themed.textMuted]}>{t('partnerCalendar.publishAt')}</Text>
              <View style={styles.pickerRow}>
                <View style={styles.flex}><PickerField mode="date" value={schedDate} onChange={setSchedDate} placeholder="YYYY-MM-DD" noMargin /></View>
                <View style={styles.flex}><PickerField mode="time" value={schedTime} onChange={setSchedTime} placeholder="09:00" noMargin /></View>
              </View>
              <TouchableOpacity
                style={[styles.action, styles.actionPrimary]}
                disabled={busy}
                onPress={() => {
                  const when = schedDate ? berlinDateTimeToMillis(schedDate, schedTime || '09:00') : null;
                  if (when === null) { setMessage({ ok: false, text: t('partnerCalendar.pickTime') }); return; }
                  void run(() => schedulePublish(raw.id, when), t('partnerCalendar.done.scheduled').replace('{date}', formatBerlinShort(when)));
                  setScheduling(false);
                }}
              >
                <Text style={[styles.actionText, styles.actionPrimaryText]}>{t('partnerCalendar.action.schedule')}</Text>
              </TouchableOpacity>
            </View>
          ) : null}
          <Text style={[styles.note, themed.textMuted]}>
            {status === 'published' ? t('partnerCalendar.note.published') : status === 'draft' || status === 'scheduled' ? t('partnerCalendar.note.draft') : status === 'cancelled' ? t('partnerCalendar.note.cancelled') : ''}
          </Text>
        </ScrollView>
      </View>
      </View>
    </Modal>
  );
};

type Themed = ReturnType<typeof createThemedStyles>;

const StatusPill: React.FC<{ status: CalendarStatus; label: string }> = ({ status, label }) => (
  <View style={[styles.pill, { backgroundColor: `${STATUS_COLORS[status]}26` }]}>
    <Text style={[styles.pillText, { color: STATUS_COLORS[status] }]}>{label}</Text>
  </View>
);

const Badge: React.FC<{ label: string; tone?: 'luma' | 'warn'; themed: Themed }> = ({ label, tone, themed }) => (
  <View style={[styles.pill, tone === 'luma' ? styles.badgeLuma : tone === 'warn' ? styles.badgeWarn : themed.badge]}>
    <Text style={[styles.pillText, tone === 'luma' ? styles.badgeLumaText : tone === 'warn' ? styles.badgeWarnText : themed.textSecondary]}>{label}</Text>
  </View>
);

const Chip: React.FC<{ label: string; active: boolean; onPress: () => void; dot?: string; themed: Themed }> = ({ label, active, onPress, dot, themed }) => (
  <TouchableOpacity onPress={onPress} style={[styles.chip, active ? themed.chipActive : themed.surface]} accessibilityRole="button" accessibilityState={{ selected: active }}>
    {dot ? <View style={[styles.chipDot, { backgroundColor: dot }]} /> : null}
    <Text style={[styles.chipText, active ? themed.chipActiveText : themed.textSecondary]}>{label}</Text>
  </TouchableOpacity>
);

const Fact: React.FC<{ label: string; value: string; themed: Themed }> = ({ label, value, themed }) => (
  <View style={styles.fact}>
    <Text style={[styles.factLabel, themed.textMuted]}>{label}</Text>
    <Text style={[styles.factValue, themed.textPrimary]}>{value}</Text>
  </View>
);

const styles = StyleSheet.create({
  container: { flex: 1 },
  flex: { flex: 1, minWidth: 0 },
  header: { flexDirection: 'row', alignItems: 'center', gap: 12, paddingHorizontal: 16, paddingBottom: 8 },
  iconButton: { width: 40, height: 40, borderRadius: 14, borderWidth: 1, alignItems: 'center', justifyContent: 'center' },
  headerCopy: { flex: 1, minWidth: 0 },
  kicker: { fontSize: 10, fontWeight: Typography.extrabold, letterSpacing: 1.6, textTransform: 'uppercase' },
  title: { fontSize: 22, fontWeight: Typography.extrabold },
  newButton: { flexDirection: 'row', alignItems: 'center', gap: 6, backgroundColor: '#F27D26', borderRadius: 999, paddingHorizontal: 14, paddingVertical: 9 },
  newButtonText: { color: '#fff', fontSize: 13, fontWeight: Typography.extrabold },
  content: { paddingHorizontal: 16, gap: 12 },
  intro: { fontSize: 13, lineHeight: 19 },
  lumaBanner: { borderRadius: 14, borderWidth: 1, padding: 12, gap: 6 },
  lumaText: { fontSize: 13, lineHeight: 19 },
  toolbar: { flexDirection: 'row', alignItems: 'center' },
  segment: { flexDirection: 'row', borderRadius: 999, borderWidth: 1, padding: 3 },
  segmentItem: { borderRadius: 999, paddingHorizontal: 16, paddingVertical: 7 },
  segmentText: { fontSize: 13, fontWeight: Typography.bold },
  filters: { flexDirection: 'row', gap: 8, flexWrap: 'nowrap' },
  chip: { flexDirection: 'row', alignItems: 'center', gap: 6, borderRadius: 999, borderWidth: 1, paddingHorizontal: 11, paddingVertical: 7 },
  chipDot: { width: 7, height: 7, borderRadius: 4 },
  chipText: { fontSize: 12, fontWeight: Typography.bold },
  loading: { marginTop: 24 },
  error: { color: '#EF4444' },
  monthNav: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', paddingHorizontal: 4 },
  monthTitle: { fontSize: 17, fontWeight: Typography.extrabold },
  grid: { flexDirection: 'row', flexWrap: 'wrap', borderRadius: 18, borderWidth: 1, padding: 6 },
  weekday: { width: `${100 / 7}%`, textAlign: 'center', fontSize: 11, fontWeight: Typography.bold, paddingVertical: 4 },
  cell: { width: `${100 / 7}%`, height: 50, alignItems: 'center', justifyContent: 'center', borderRadius: 12, gap: 3 },
  cellNum: { fontSize: 14, fontWeight: Typography.bold, minWidth: 26, textAlign: 'center', borderRadius: 13, overflow: 'hidden', paddingVertical: 2 },
  cellOutside: { opacity: 0.35 },
  cellToday: { backgroundColor: '#F27D26', color: '#fff' },
  dots: { flexDirection: 'row', gap: 3, height: 6 },
  dot: { width: 6, height: 6, borderRadius: 3 },
  dayPanelHead: { flexDirection: 'row', alignItems: 'center', justifyContent: 'space-between', marginTop: 4 },
  addDay: { flexDirection: 'row', alignItems: 'center', gap: 5, borderRadius: 999, borderWidth: 1, paddingHorizontal: 12, paddingVertical: 7 },
  addDayText: { fontSize: 12, fontWeight: Typography.extrabold },
  day: { gap: 8 },
  dayHeading: { fontSize: 13, fontWeight: Typography.extrabold },
  row: { flexDirection: 'row', alignItems: 'center', gap: 10, borderRadius: 16, borderWidth: 1, paddingVertical: 10, paddingRight: 12, overflow: 'hidden' },
  rowBar: { width: 4, alignSelf: 'stretch', borderRadius: 2 },
  rowTime: { width: 46 },
  rowTimeText: { fontSize: 13, fontWeight: Typography.extrabold },
  rowTimeEnd: { fontSize: 11 },
  rowMain: { flex: 1, minWidth: 0, gap: 3 },
  rowTitle: { fontSize: 14, fontWeight: Typography.extrabold },
  strike: { textDecorationLine: 'line-through' },
  rowMeta: { fontSize: 12 },
  badges: { flexDirection: 'row', flexWrap: 'wrap', gap: 5, marginTop: 2 },
  pill: { borderRadius: 999, paddingHorizontal: 8, paddingVertical: 2 },
  pillText: { fontSize: 10.5, fontWeight: Typography.extrabold },
  badgeLuma: { backgroundColor: 'rgba(168,85,247,0.16)' },
  badgeLumaText: { color: '#A855F7' },
  badgeWarn: { backgroundColor: 'rgba(251,191,36,0.16)' },
  badgeWarnText: { color: '#D97706' },
  empty: { fontSize: 13, textAlign: 'center', paddingVertical: 16 },
  portalLink: { paddingVertical: 12 },
  portalText: { fontSize: 12, textAlign: 'center' },
  sheetRoot: { flex: 1, justifyContent: 'flex-end' },
  sheetBackdrop: { backgroundColor: 'rgba(0,0,0,0.45)' },
  sheet: { maxHeight: '88%', borderTopLeftRadius: 24, borderTopRightRadius: 24, borderWidth: 1, paddingTop: 8 },
  grabber: { alignSelf: 'center', width: 40, height: 4, borderRadius: 2, marginBottom: 8 },
  sheetContent: { paddingHorizontal: 18, gap: 10, paddingBottom: 8 },
  cover: { width: '100%', aspectRatio: 16 / 9, borderRadius: 14 },
  sheetTitle: { fontSize: 20, fontWeight: Typography.extrabold },
  fact: { flexDirection: 'row', gap: 10 },
  factLabel: { width: 74, fontSize: 13 },
  factValue: { flex: 1, fontSize: 13, fontWeight: Typography.semibold },
  errorBox: { color: '#EF4444', fontSize: 13, backgroundColor: 'rgba(239,68,68,0.1)', borderRadius: 10, padding: 10, overflow: 'hidden' },
  warnBox: { color: '#D97706', fontSize: 13, backgroundColor: 'rgba(251,191,36,0.12)', borderRadius: 10, padding: 10, overflow: 'hidden' },
  scopeBox: { borderRadius: 14, borderWidth: 1, padding: 12, gap: 8 },
  scopeLegend: { fontSize: 12, fontWeight: Typography.bold },
  scopeRow: { flexDirection: 'row', alignItems: 'center', gap: 8 },
  scopeText: { fontSize: 14, fontWeight: Typography.semibold },
  message: { borderRadius: 10, padding: 10, fontSize: 13, fontWeight: Typography.semibold, overflow: 'hidden' },
  messageOk: { backgroundColor: 'rgba(34,197,94,0.14)', color: '#16A34A' },
  messageError: { backgroundColor: 'rgba(239,68,68,0.12)', color: '#EF4444' },
  actions: { flexDirection: 'row', flexWrap: 'wrap', gap: 8 },
  action: { flexGrow: 1, minWidth: '45%', borderRadius: 14, borderWidth: 1, paddingVertical: 12, alignItems: 'center' },
  actionPrimary: { backgroundColor: '#F27D26', borderColor: '#F27D26' },
  actionText: { fontSize: 14, fontWeight: Typography.extrabold },
  actionPrimaryText: { color: '#fff' },
  dangerText: { color: '#EF4444' },
  disabled: { opacity: 0.45 },
  pickerRow: { flexDirection: 'row', gap: 8 },
  note: { fontSize: 12, lineHeight: 17 },
});

const createThemedStyles = (theme: TonyteTheme) => StyleSheet.create({
  container: { backgroundColor: theme.colors.background },
  textPrimary: { color: theme.colors.textPrimary },
  textSecondary: { color: theme.colors.textSecondary },
  textMuted: { color: theme.colors.textMuted },
  accentText: { color: theme.colors.accentSecondary },
  surface: { backgroundColor: theme.colors.surfaceElevated, borderColor: theme.colors.border },
  sheet: { backgroundColor: theme.isDark ? '#111826' : theme.colors.surfaceElevated, borderColor: theme.colors.border },
  segmentActive: { backgroundColor: theme.isDark ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.06)' },
  chipActive: { backgroundColor: theme.colors.accentSecondary, borderColor: theme.colors.accentSecondary },
  chipActiveText: { color: theme.isDark ? '#04201F' : '#FFFFFF' },
  cellSelected: { backgroundColor: theme.isDark ? 'rgba(38,208,206,0.16)' : 'rgba(21,155,154,0.12)' },
  badge: { backgroundColor: theme.isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)' },
  lumaBanner: { backgroundColor: 'rgba(168,85,247,0.08)', borderColor: 'rgba(168,85,247,0.3)' },
});

export default PartnerCalendarScreen;

